// react
import { useMemo } from 'react';
import styled from 'styled-components';

// components
import { Button, Card, ProgressBar, Title } from '@/components/atoms';
import { BackButton } from '@/components/molecules';
import { CardContainer } from '@/components/templates';

// hooks
import { useWindowSize } from '@/hooks';

// theme
import theme from '@/theme';

// assets
// @transform-path ../../../../../../src/assets/llama-group.svg
import llamaGroup from '@/assets/llama-group.svg';

/**
 * RecipientProfileCarePlan shows the profile of the care recipient.
 */
export const RecipientProfileCarePlan = ({
  firstName,
  progress,
  toCareProfile,
  limit,
  toBookCall,
}: RecipientProfileCarePlanProps) => {
  // hooks
  const [width] = useWindowSize();

  const isComplete = useMemo(() => {
    if (progress === undefined || limit === undefined) return false;
    return progress >= limit;
  }, [progress, limit]);

  return (
    <Body>
      <BackButton />
      <Title>{firstName || 'Your loved one'}'s Care Assessment</Title>
      <HelperText>
        The KareHero Care Assessment will help you record{' '}
        {firstName || 'your loved one'}’s care needs so you can find suitable
        care providers and ensure health professionals are taking into account
        their needs.
      </HelperText>
      {progress === undefined || progress === 0 ? (
        <CardContainer maxColumns={1} isPreventHeightMatch>
          <StyledCard color='green'>
            <CareProfileCardBody>
              <CardContent>
                <CardTitle>Get ready for care</CardTitle>
                <CardBody>
                  Complete a series of questions about{' '}
                  {firstName || 'your loved one'}’s care needs. You may need to
                  collect information from various sources, so you can save and
                  come back to this form at anytime.
                </CardBody>
              </CardContent>
              <Button ariaLabel='start my care assessment' to={toCareProfile}>
                Start care assessment
              </Button>
            </CareProfileCardBody>
          </StyledCard>
          <StyledCard color='blue'>
            <CareProfileCardBody>
              <CardContent>
                <CardTitle>Information you need</CardTitle>
                <CardBody>
                  <ul>
                    <li>Care professionals' details</li>
                    <li>Health history</li>
                    <li>Condition status</li>
                    <li>Mobility status</li>
                    <li>Medication details</li>
                  </ul>
                </CardBody>
              </CardContent>
            </CareProfileCardBody>
          </StyledCard>
        </CardContainer>
      ) : (
        <CardContainer maxColumns={1} isPreventHeightMatch>
          <StyledCard color={isComplete ? 'blue' : 'green'}>
            <CareProfileCardWrapper>
              <CareProfileCardBody>
                <CardContent>
                  <CardTitle>
                    {isComplete
                      ? "Well done! You've completed the care assessment"
                      : "You're doing great!"}
                  </CardTitle>
                  <CardBody>
                    {isComplete
                      ? 'You can now book a call back to discuss your care assessment.'
                      : 'Continue filling in your personal care assessment.'}
                  </CardBody>
                </CardContent>
                <ProgressBar
                  label='Progress'
                  limit={limit || 0}
                  progress={progress}
                />
                <ActionGroup>
                  {isComplete ? (
                    <>
                      <Button
                        ariaLabel='view care assessment'
                        variant='secondary'
                        to={toCareProfile}
                      >
                        View care assessment
                      </Button>
                      <Button ariaLabel='Book a call back' to={toBookCall}>
                        Book call back
                      </Button>
                    </>
                  ) : (
                    <Button
                      ariaLabel='continue care assessment'
                      to={toCareProfile}
                    >
                      Continue care assessment
                    </Button>
                  )}
                </ActionGroup>
              </CareProfileCardBody>
              {width > theme.breakpoint.lgValue && (
                <img src={llamaGroup} alt='llama group' />
              )}
            </CareProfileCardWrapper>
          </StyledCard>
          {!isComplete && (
            <StyledCard color='blue'>
              <CareProfileCardBody>
                <CardContent>
                  <CardTitle>Information you need</CardTitle>
                  <CardBody>
                    <ul>
                      <li>Care professionals' details</li>
                      <li>Health history</li>
                      <li>Condition status</li>
                      <li>Mobility status</li>
                      <li>Medication details</li>
                    </ul>
                  </CardBody>
                </CardContent>
              </CareProfileCardBody>
            </StyledCard>
          )}
        </CardContainer>
      )}
    </Body>
  );
};

const Body = styled.div`
  padding-bottom: ${(props) => props.theme.spacing[24]};
`;

const HelperText = styled.div`
  font: ${(props) => props.theme.typography.body.md.regular};
  color: ${(props) => props.theme.color.neutral[80]};
  padding-bottom: ${(props) => props.theme.spacing[36]};
`;

const StyledCard = styled(Card)`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  gap: ${(props) => props.theme.spacing[32]};
  align-items: flex-start;
  color: ${(props) => props.theme.color.neutral[80]};
  border-radius: 8px;
`;

const CareProfileCardBody = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  align-items: flex-start;
  gap: ${(props) => props.theme.spacing[32]};
  flex: 1;
`;

const CareProfileCardWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  width: 100%;
  height: 100%;
  gap: ${(props) => props.theme.spacing[16]};
`;

const CardContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${(props) => props.theme.spacing[20]};
`;

const CardTitle = styled.div`
  font: ${(props) => props.theme.typography.heading['3xl']};
  color: ${(props) => props.theme.color.neutral[100]};
`;

const CardBody = styled.div`
  font: ${(props) => props.theme.typography.body.md.regular};
  color: ${(props) => props.theme.color.neutral[100]};

  & > ul {
    margin-bottom: 0;
  }
`;

const ActionGroup = styled.div`
  display: flex;
  gap: ${(props) => props.theme.spacing[16]};
`;

interface RecipientProfileCarePlanProps {
  firstName?: string;
  limit?: number;
  progress?: number;
  toCareProfile?: string;
  toBookCall?: string;
}

RecipientProfileCarePlan.displayName = 'RecipientProfileCarePlan';

export default RecipientProfileCarePlan;
